import { useSelector } from 'react-redux';
import { ClaimAttachment } from '@1po/1po-bff-fe-spec/generated/claim/request/CreateOrderClaimRequest';
import { getIsVrnSearchLoading, getOrderClaimForm } from 'domains/order/Order.store';
import { ClaimForm, ClaimLabourInfo, ClaimVehicleInfo, FULL_OTC } from 'domains/order/Order.types';
import { getFormGroupType } from 'domains/order/Order.utils';
import { FormOption, makeKey, RULES_DATA, RulesData } from 'pages/MyOrdersPage/OrderClaims/claimFormRulesData';
import { isValidTime } from 'utils/date';

type ClaimFieldValue = string | number | ClaimVehicleInfo | ClaimLabourInfo | ClaimAttachment[];

export const isHidden = (s?: FormOption): boolean => {
  if (!s) {
    return true;
  }
  return s === 'hidden';
};

export const isMandatory = (s?: FormOption): boolean => {
  if (!s) {
    return true;
  }
  return s === 'mandatory';
};

const isQuantityField = (field: string): boolean =>
  quantityFieldNames.includes(field as typeof quantityFieldNames[number]);

const isPrimitiveFilled = (value?: string | number | ClaimAttachment[]): boolean => {
  if (value === undefined || value === null) {
    return false;
  }

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (typeof value === 'number') {
    return value > 0;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return false;
};

const isFilled = (field: string, value?: ClaimFieldValue): boolean => {
  if (isQuantityField(field)) {
    return typeof value === 'number' && value >= 0;
  }

  if (value === undefined || value === null) {
    return false;
  }

  if (field === 'vehicle') {
    return isFilledVehicle(value as ClaimVehicleInfo);
  }

  if (field === 'labour') {
    return isFilledLabour(value as ClaimLabourInfo);
  }

  return isPrimitiveFilled(value as string | number | ClaimAttachment[]);
};

const isFilledVehicle = (value: ClaimVehicleInfo): boolean => {
  if (!isFilled('vehicle.vrn', value.vrn)) {
    return false;
  }
  const additionalFields = [value.registrationDate, value.brand, value.model, value.energy, value.engineCode];
  return additionalFields.some((field) => isFilled('vehicle.field', field));
};

const allTrue = (checks: boolean[]): boolean => checks.every(Boolean);

const isFilledLabour = (value: ClaimLabourInfo): boolean => {
  if (!value.isFitted) {
    return true;
  }

  return allTrue([
    isFilled('labour.labourTime', value.labourTime),
    isValidTime(value.labourTime ?? ''),
    isFilled('labour.fittingDate', value.fittingDate),
    isFilled('labour.fittingMileage', value.fittingMileage),
    isFilled('labour.dismantlingDate', value.dismantlingDate),
    isFilled('labour.dismantlingMileage', value.dismantlingMileage),
  ]);
};

export const getTextMandatory = (text: string, option?: FormOption): string => {
  return getTextWithMandatoryFlag(text, isMandatory(option));
};

export const getTextWithMandatoryFlag = (text: string, mandatory: boolean): string => {
  return mandatory ? `${text}(*)` : text;
};

export const isMessageMandatory = (claimForm: ClaimForm): boolean => {
  if (claimForm.reason === 'Y9G' && claimForm.symptom === '10') {
    return true;
  }
  const rulesData =
    RULES_DATA[makeKey({ formType: claimForm.formType, reason: claimForm.reason, option: claimForm.option })];
  return isMandatory(rulesData?.message);
};

export const useIsReceivedReferenceDifferentFromOrdered = (): boolean => {
  const claimForm = useSelector(getOrderClaimForm);
  if (getFormGroupType(claimForm.formType) !== FULL_OTC || claimForm.reason !== 'Y05') {
    return true;
  }
  if (!claimForm.referenceNumberReceived || !claimForm.referenceNumberOrdered) {
    return true;
  }
  return (
    claimForm.referenceNumberReceived.trim().toLowerCase() !== claimForm.referenceNumberOrdered.trim().toLowerCase()
  );
};

export const useIsClaimFormValid = (): boolean => {
  const claimForm = useSelector(getOrderClaimForm);
  const isVrnSearchLoading = useSelector(getIsVrnSearchLoading);
  const isReceivedRefValid = useIsReceivedReferenceDifferentFromOrdered();
  const rulesData =
    RULES_DATA[makeKey({ formType: claimForm.formType, reason: claimForm.reason, option: claimForm.option })];

  if (!rulesData) {
    return false;
  }

  // Block submission while vehicle data is being fetched (only relevant when vehicle section is visible)
  if (isVrnSearchLoading && !isHidden(rulesData.vehicle)) {
    return false;
  }

  //Check mandatory fields
  const mandatoryFieldsValid = (Object.entries(rulesData) as [keyof RulesData, FormOption][])
    .filter(([, rule]) => isMandatory(rule))
    .every(([field]) => isFilled(field, claimForm[field]));

  if (!mandatoryFieldsValid || !isReceivedRefValid) {
    return false;
  }

  // Special case: for Y9G with symptom '10-Other', message is additionally mandatory
  const isOtherSymptomMessageMandatory = claimForm.reason === 'Y9G' && claimForm.symptom === '10';
  return !(isOtherSymptomMessageMandatory && !isFilled('message', claimForm.message));
};

export const useIsClaimedQuantityComputed = (): boolean => {
  const claimForm = useSelector(getOrderClaimForm);
  switch (claimForm.reason) {
    case 'Y01':
    case 'Y01N':
    case 'Y02':
    case 'Y08':
      return claimForm.quantityInvoiced !== undefined && claimForm.quantityInvoiced !== null;
    default:
      return false;
  }
};

const quantityFieldNames = ['quantityClaimed', 'quantityInvoiced', 'quantityReceived'] as const;
